import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Entity, EntityKey } from '../entity/entity';
import { EntityLineHeader } from '../entity/entity-line-header';
import { EntitySaveResponse } from '../entity/entity-save-response';
import { ReleaseLine } from '../orders/release/release-line';
import { ReleaseLineDetail } from '../orders/release/release-line-detail';
import { ReleaseSingleEntityLine } from '../orders/release/release-single-entity-line';
import { ScheduleReleaseSingleEntityLine } from "../orders/release/schedule-release-single-entity-line";
import { GoogleService } from './google-service';

@Service()
export class OrderService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl;

  public save(order: Entity): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'orders/save';
    let body = JSON.stringify(order);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  public runMultiplier(id: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'orders/multiplier';
    return this.getEntityResponse(id, url);
  }

  isRepriceNeeded(id: EntityKey): Observable<string> {
    const url = this.urlBase + 'orders/repriceNeeded/' + id.entityId + '/' + id.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  reprice(id: EntityKey): Observable<Entity> {
    const url = this.urlBase + 'orders/reprice';
    return this.getEntityResponse(id, url);
  }

  private getEntityResponse(id: EntityKey, url: string): Observable<Entity> {
    let body = JSON.stringify(id);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public sendAcknowledgment(id: EntityKey): Observable<string> {
    let url = this.urlBase + 'orders/sendAcknowledgment';
    return this.getStringResponse(id, url);
  }

  private getStringResponse(id: EntityKey, url: string): Observable<string> {
    let body = JSON.stringify(id);
    return this.http.post(url, body, {headers: this.getPostHeaders(), responseType: 'text'});
  }

  public sendToScheduling(id: EntityKey): Observable<string> {
    let url = this.urlBase + 'orders/sendToScheduling';
    return this.getStringResponse(id, url);
  }

  public releasePendingApproval(id: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'orders/releasePendingApproval';
    return this.getEntityResponse(id, url);
  }

  public cancel(id: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'orders/cancel';
    return this.getEntityResponse(id, url);
  }

  public revertCancel(id: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'orders/revertCancel';
    return this.getEntityResponse(id, url);
  }

  public cancelLines(cancelReason: string, lines: EntityLineHeader[]): Observable<Entity> {
    let url = this.urlBase + 'orders/cancelLines/' + cancelReason;
    let body = JSON.stringify(lines);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public generateRevision(id: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'orders/generateRevision';
    return this.getEntityResponse(id, url);
  }

  public getReleaseLines(id: string, revision: number): Observable<ReleaseLine[]> {
    let url = this.urlBase + 'orders/releaseLines/' + id + '/' + revision;
    return this.http.get<ReleaseLine[]>(url, {headers: this.getRequestHeaders()});
  }

  public getLineDetails(line: ReleaseLine): Observable<ReleaseLineDetail[]> {
    let url = this.urlBase + 'orders/lineDetails';
    let body = JSON.stringify(line);
    return this.http.post<ReleaseLineDetail[]>(url, body, {headers: this.getPostHeaders()});
  }

  public releaseByLine(lines: ReleaseLine[]): Observable<Entity> {
    let url = this.urlBase + 'orders/release/line';
    return this.getReleaseResponse(url, lines);
  }

  private getReleaseResponse(url: string, lines: ReleaseLine[]): Observable<Entity> {
    let body = JSON.stringify(lines);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public releaseByTag(lines: ReleaseLine[]): Observable<Entity> {
    let url = this.urlBase + 'orders/release/tag';
    return this.getReleaseResponse(url, lines);
  }

  public getReleases(entityKey: EntityKey): Observable<EntityKey[]> {
    let url = this.urlBase + 'orders/releases/' + entityKey.entityId + '/' + entityKey.entityRevision;
    return this.http.get<EntityKey[]>(url, {headers: this.getRequestHeaders()});
  }

  public submitForReview(id: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'orders/submitForReview';
    let body = JSON.stringify(id);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public getModReleaseUnits(id: EntityKey): Observable<ReleaseSingleEntityLine[]> {
    let url = this.urlBase + 'orders/modRelease/' + id.entityId + '/' + id.entityRevision;
    return this.http.get<ReleaseSingleEntityLine[]>(url, {headers: this.getRequestHeaders()});
  }

  public getScheduleReleaseInformation(id: EntityKey): Observable<ScheduleReleaseSingleEntityLine[]> {
    let url = this.urlBase + 'orders/scheduleRelease/' + id.entityId + '/' + id.entityRevision;
    return this.http.get<ScheduleReleaseSingleEntityLine[]>(url, {headers: this.getRequestHeaders()});
  }

  public saveScheduleReleaseDates(entityKey: EntityKey, scheduleReleases: ScheduleReleaseSingleEntityLine[], allReleasesScheduled: boolean): Observable<string> {
    let url = this.urlBase + 'orders/saveScheduleReleaseDates/' + entityKey.entityId + '/' + entityKey.entityRevision + '/' + allReleasesScheduled;
    let body = JSON.stringify(scheduleReleases);
    return this.http.post(url, body, {headers: this.getPostHeaders(), responseType: 'text'});
  }

  public releaseModUnits(id: EntityKey, units: ReleaseSingleEntityLine[]): Observable<Entity> {
    let url = this.urlBase + 'orders/generateModRelease/' + id.entityId + '/' + id.entityRevision;
    let body = JSON.stringify(units);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public getCreditExceptionDescription(code: string): Observable<string> {
    let url = this.urlBase + 'orders/creditExceptionDescription/' + code;
    return this.http.get(url, {headers: this.getRequestHeaders(), observe: "body", responseType: "text"});
  }

  public getMockupPendingStatus(order: Entity): Observable<boolean> {
    let url = this.urlBase + 'orders/isMockupApprovalPending';
    let body = JSON.stringify(order);
    return this.http.post(url, body, {headers: this.getPostHeaders()}).pipe(map(response => {
      return 'true' === response;
    }))
  }

}
